/**
 * Unified Parser with Three-Tier Fallback Strategy
 * 
 * Tier 1: LLM Direct HTML (existing "Generate" tab)
 * Tier 2: JSON + Regex Renderer (current implementation)  
 * Tier 3: Tree-sitter AST (future, like VS Code)
 * 
 * Automatic fallback: 3 → 2 → 1
 */

import { AnnotatedCodeExample, SimpleCodeExample } from '../types';
import { generateAnnotatedCode } from './codeTemplateParser';
import { parseWithRealTreeSitter, isTreeSitterAvailable } from './realTreeSitter';
import { AIService } from '../services';

// Unified result interface
export interface ParseResult {
  success: boolean;
  method: 'llm-html' | 'json-regex' | 'tree-sitter';
  html?: string;           // For Tier 1 (LLM HTML)
  annotatedCode?: AnnotatedCodeExample;  // For Tier 2 & 3
  confidence: number;      // 0-1 quality score
  parseTime: number;       // milliseconds
  error?: string;
}

// Parser configuration
export interface ParserConfig {
  preferredMethod?: 'auto' | 'llm-html' | 'json-regex' | 'tree-sitter';
  enableFallback: boolean;
  timeoutMs: number;
}

/**
 * Tier 1: LLM Direct HTML Generation  
 * Uses backend AI service (same as Generate tab)
 */
async function parseWithLLM(
  code: string, 
  knownLanguage: string, 
  targetLanguage: string,
  concept?: string
): Promise<ParseResult> {
  const startTime = performance.now();
  
  try {
    console.log('🤖 Using backend AI service for lens HTML generation');
    
    const aiService = new AIService();
    const result = await aiService.generateLensHTML(code, knownLanguage, targetLanguage, concept);
    
    return {
      success: true,
      method: 'llm-html',
      html: result.html,
      confidence: 0.85, // LLM is good but not perfect
      parseTime: performance.now() - startTime
    };
    
  } catch (error) {
    console.error('❌ LLM generation failed:', error);
    return {
      success: false,
      method: 'llm-html',
      confidence: 0,
      parseTime: performance.now() - startTime,
      error: error instanceof Error ? error.message : 'LLM generation failed'
    };
  }
}

/**
 * Tier 2: JSON + Regex Renderer (Current Implementation)
 * Uses existing codeTemplateParser with validation
 */
async function parseWithRegex(example: SimpleCodeExample): Promise<ParseResult> {
  const startTime = performance.now();
  
  try {
    const annotatedCode = generateAnnotatedCode(example);
    
    // Simple validation: check if lens targets were found
    const totalLenses = Object.keys(example.lenses).length;
    const foundLenses = annotatedCode.lines.reduce((count, line) => 
      count + line.tokens.filter(token => token.annotation).length, 0
    );
    
    const confidence = foundLenses / totalLenses; // 0-1 based on match rate
    
    return {
      success: true,
      method: 'json-regex',
      annotatedCode,
      confidence,
      parseTime: performance.now() - startTime
    };
    
  } catch (error) {
    return {
      success: false,
      method: 'json-regex',
      confidence: 0,
      parseTime: performance.now() - startTime,
      error: error instanceof Error ? error.message : 'Regex parsing failed'
    };
  }
}

/**
 * Tier 3: Tree-sitter AST (Real Implementation)
 * Client-side approach like VS Code, Monaco Editor
 */
async function parseWithTreeSitter(example: SimpleCodeExample): Promise<ParseResult> {
  const startTime = performance.now();
  
  // Check if Tree-sitter is available for this language
  console.log(`🔍 Checking Tree-sitter availability for ${example.targetLanguage}...`);
  const isAvailable = await isTreeSitterAvailable(example.targetLanguage);
  if (!isAvailable) {
    console.log(`❌ Tree-sitter not available for ${example.targetLanguage}`);
    return {
      success: false,
      method: 'tree-sitter',
      confidence: 0,
      parseTime: performance.now() - startTime,
      error: `Tree-sitter not available for ${example.targetLanguage}`
    };
  }
  
  console.log(`✅ Tree-sitter available for ${example.targetLanguage}`);
  
  try {
    console.log('🌳 Using real Tree-sitter for', example.targetLanguage);
    
    // Parse with real Tree-sitter
    const parseResult = await parseWithRealTreeSitter(example.code, example.targetLanguage);
    
    if (!parseResult.success) {
      throw new Error(parseResult.error || 'Tree-sitter parsing failed');
    }
    
    // Convert to AnnotatedCodeExample format
    const annotatedCode = convertTreeSitterToAnnotated(parseResult, example);
    
    return {
      success: true,
      method: 'tree-sitter',
      annotatedCode,
      confidence: 0.98, // Tree-sitter is very accurate
      parseTime: performance.now() - startTime
    };
    
  } catch (error) {
    console.error('❌ Tree-sitter parsing failed:', error);
    return {
      success: false,
      method: 'tree-sitter',
      confidence: 0,
      parseTime: performance.now() - startTime,
      error: error instanceof Error ? error.message : 'Tree-sitter parsing failed'
    };
  }
}

/**
 * Main Unified Parser with Automatic Fallback
 * Tier 3 → Tier 2 → Tier 1
 */
export async function parseCode(
  input: SimpleCodeExample | {
    code: string;
    knownLanguage: string;
    targetLanguage: string;
    concept?: string;
  },
  config: ParserConfig = {
    preferredMethod: 'auto',
    enableFallback: true,
    timeoutMs: 5000
  }
): Promise<ParseResult> {
  
  console.log('🔍 Starting unified parsing with fallback strategy');
  
  const isSimpleExample = 'lenses' in input;
  
  // Auto-detect best method based on input
  if (config.preferredMethod === 'auto') {
    if (isSimpleExample) {
      // Has lens definitions → try Tree-sitter, fallback to Regex
      config.preferredMethod = 'tree-sitter';
    } else {
      // Just code + concept → try LLM first
      config.preferredMethod = 'llm-html';
    }
  }
  
  const methods: Array<'tree-sitter' | 'json-regex' | 'llm-html'> = [];
  
  // Build fallback chain based on preference
  if (config.preferredMethod === 'tree-sitter') {
    methods.push('tree-sitter', 'json-regex');
    if (!isSimpleExample) methods.push('llm-html');
  } else if (config.preferredMethod === 'json-regex') {
    methods.push('json-regex');
    if (!isSimpleExample) methods.push('llm-html');
  } else if (config.preferredMethod === 'llm-html') {
    if (!isSimpleExample) methods.push('llm-html');
    if (isSimpleExample) methods.push('json-regex');
  }
  
  // Try each method in order
  for (const method of methods) {
    console.log(`🔄 Trying ${method} parsing...`);
    
    let result: ParseResult;
    
    try {
      if (method === 'tree-sitter' && isSimpleExample) {
        result = await parseWithTreeSitter(input as SimpleCodeExample);
      } else if (method === 'json-regex' && isSimpleExample) {
        result = await parseWithRegex(input as SimpleCodeExample);
      } else if (method === 'llm-html' && !isSimpleExample) {
        const { code, knownLanguage, targetLanguage, concept } = input as any;
        result = await parseWithLLM(code, knownLanguage, targetLanguage, concept);
      } else {
        continue; // Skip incompatible method
      }
      
      if (result.success && result.confidence > 0.5) {
        console.log(`✅ Success with ${method} (confidence: ${result.confidence.toFixed(2)})`);
        return result;
      } else if (result.success) {
        console.log(`⚠️ Low confidence with ${method} (${result.confidence.toFixed(2)}), trying next...`);
      } else {
        console.log(`❌ Failed with ${method}: ${result.error}`);
      }
      
    } catch (error) {
      console.log(`💥 Exception with ${method}:`, error);
    }
    
    if (!config.enableFallback) {
      break; // Don't try other methods
    }
  }
  
  // All methods failed
  return {
    success: false,
    method: 'llm-html', // Default
    confidence: 0,
    parseTime: 0,
    error: 'All parsing methods failed'
  };
}

/**
 * Convert Tree-sitter ParseResult to AnnotatedCodeExample
 */
function convertTreeSitterToAnnotated(parseResult: any, example: SimpleCodeExample): AnnotatedCodeExample {
  console.log('🔍 Converting Tree-sitter result to annotated code');
  console.log('🎯 Lens keys to match:', Object.keys(example.lenses));
  
  // Map Tree-sitter tokens to AnnotatedCodeExample format with smarter lens matching
  const annotatedLines = parseResult.lines.map((astLine: any) => {
    console.log(`📝 Processing line ${astLine.lineNumber}:`, astLine.fullText);
    console.log('🔤 Line tokens:', astLine.tokens.map((t: any) => `"${t.text}" (${t.type})`));
    
    // Smart lens matching: check for pattern combinations and AST structure
    const tokensWithAnnotations = matchLensesInTokens(astLine.tokens, example.lenses);
    
    return {
      lineNumber: astLine.lineNumber,
      tokens: tokensWithAnnotations
    };
  });
  
  return {
    id: example.id,
    title: `${example.title} (Tree-sitter AST)`,
    description: `${example.description} - Parsed with real Tree-sitter`,
    knownLanguage: example.knownLanguage,
    targetLanguage: example.targetLanguage,
    lines: annotatedLines,
    lensletIds: Object.keys(example.lenses)
  };
}


/**
 * Utility: Convert LLM HTML to work with SyntaxLensWidget
 */
export function prepareLLMHtmlForWidget(html: string): string {
  // Add necessary classes and ensure hoverable elements work
  return html.replace(/class="([^"]*hoverable[^"]*)"/g, 'class="$1 hoverable"');
}

/**
 * Performance metrics for comparison
 */
export function getParsingStats(results: ParseResult[]): any {
  const stats = {
    totalAttempts: results.length,
    successfulMethods: {} as Record<string, number>,
    averageParseTime: {} as Record<string, number>,
    averageConfidence: {} as Record<string, number>
  };
  
  results.forEach(result => {
    const method = result.method;
    
    if (result.success) {
      stats.successfulMethods[method] = (stats.successfulMethods[method] || 0) + 1;
    }
    
    stats.averageParseTime[method] = (stats.averageParseTime[method] || 0) + result.parseTime;
    stats.averageConfidence[method] = (stats.averageConfidence[method] || 0) + result.confidence;
  });
  
  // Calculate averages
  Object.keys(stats.averageParseTime).forEach(method => {
    const count = results.filter(r => r.method === method).length;
    stats.averageParseTime[method] /= count;
    stats.averageConfidence[method] /= count;
  });
  
  return stats;
}

/**
 * Smart lens matching for Tree-sitter tokens
 * Handles pattern combinations like "Box::new" and AST-based matching
 */
function matchLensesInTokens(tokens: any[], lensDefinitions: any): any[] {
  const annotatedTokens: any[] = [];
  const lensKeys = Object.keys(lensDefinitions);
  
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    
    // Create base annotated token with proper CSS classes
    const annotatedToken: any = {
      text: token.text,
      className: token.className || getDefaultSyntaxClass(token)
    };
    
    // Strategy 1: Direct token match (e.g., "user")
    const directMatch = lensKeys.find(key => token.text === key);
    if (directMatch) {
      console.log(`✅ Direct match: "${token.text}" -> lens "${directMatch}"`);
      annotatedToken.annotation = createLensAnnotation(directMatch, lensDefinitions[directMatch], token);
    }
    
    // Strategy 2: AST-based match (e.g., scoped_identifier for "Box::new")
    else if (token.type === 'scoped_identifier' && token.text) {
      const scopedMatch = lensKeys.find(key => token.text === key);
      if (scopedMatch) {
        console.log(`✅ Scoped identifier match: "${token.text}" -> lens "${scopedMatch}"`);
        annotatedToken.annotation = createLensAnnotation(scopedMatch, lensDefinitions[scopedMatch], token);
      }
    }
    
    // Strategy 3: Pattern combination (e.g., "Box" + "::" + "new")
    else if (!annotatedToken.annotation) {
      const combinedMatch = findCombinedPattern(tokens, i, lensKeys);
      if (combinedMatch) {
        console.log(`✅ Combined pattern match: "${combinedMatch.pattern}" -> lens "${combinedMatch.lensKey}"`);
        
        // Mark all tokens in the pattern as part of this lens
        for (let j = i; j < i + combinedMatch.tokenCount; j++) {
          if (j === i) {
            // First token gets the annotation
            annotatedToken.annotation = createLensAnnotation(combinedMatch.lensKey, lensDefinitions[combinedMatch.lensKey], tokens[j]);
          }
          // All tokens get updated CSS classes to show they're part of a lens
          annotatedTokens.push({
            text: tokens[j].text,
            className: tokens[j].className || getDefaultSyntaxClass(tokens[j]),
            ...(j === i ? { annotation: annotatedToken.annotation } : {})
          });
        }
        i += combinedMatch.tokenCount - 1; // Skip the processed tokens
        continue;
      }
    }
    
    annotatedTokens.push(annotatedToken);
  }
  
  return annotatedTokens;
}

/**
 * Create lens annotation object
 */
function createLensAnnotation(lensKey: string, lensData: any, token: any) {
  return {
    element: lensKey,
    className: token.className || '',
    staticData: lensData,
    position: {
      line: token.startPosition.row + 1,
      startChar: token.startPosition.column,
      endChar: token.endPosition.column
    },
    // Tree-sitter specific data
    nodeId: token.nodeId,
    nodeType: token.type,
    startByte: token.startByte,
    endByte: token.endByte
  };
}

/**
 * Find combined patterns like "Box::new", "String::from"
 */
function findCombinedPattern(tokens: any[], startIndex: number, lensKeys: string[]): any {
  // Look for patterns up to 5 tokens ahead
  for (let len = 2; len <= Math.min(5, tokens.length - startIndex); len++) {
    let pattern = '';
    for (let j = 0; j < len; j++) {
      pattern += tokens[startIndex + j].text;
    }
    
    const matchingKey = lensKeys.find(key => key === pattern);
    if (matchingKey) {
      return { 
        lensKey: matchingKey, 
        pattern: pattern, 
        tokenCount: len 
      };
    }
  }
  
  return null;
}

/**
 * Get default CSS class for syntax highlighting if Tree-sitter didn't provide one
 */
function getDefaultSyntaxClass(token: any): string {
  // This matches what the existing JSON-regex parser uses
  if (token.type.includes('comment')) return 'comment';
  if (token.type.includes('string') || token.type.includes('literal')) return 'string';  
  if (token.type.includes('number')) return 'number';
  if (token.type === 'identifier') return 'variable';
  if (token.type.includes('function') || token.type.includes('call')) return 'function';
  if (token.type.includes('type')) return 'type-keyword';
  if (token.type.includes('keyword') || ['fn', 'let', 'mut', 'const'].includes(token.text)) return 'keyword';
  if (/^[{}()[\];,.:]+$/.test(token.text)) return 'text-gray-300';
  if (/^[+\-*/%=<>!&|^~]+$/.test(token.text)) return 'operator';
  
  return 'variable';
}